import { useState, CSSProperties, useEffect } from 'react'
import { useEffectOnce } from 'usehooks-ts'
import styled from 'styled-components'
import ReactECharts from 'components/ReactECharts'
import { color, type EChartsOption, type SetOptionOpts } from 'echarts'
import * as d3 from 'd3'
import { nest } from 'd3-collection'
import './tooltip.scss'

export interface ReactEChartsProps {
  option: EChartsOption
  style?: CSSProperties
  settings?: SetOptionOpts
  loading?: boolean
  theme?: string
}

const BarChart = ({
  tenant,
  colorMode,
  data,
}: {
  tenant?: object
  colorMode?: string
  data: any
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const [chartOptions, setChartOptions] = useState({})
  let allYears: Array<string>

  const handleResize = (): void => {
    setIsMobile(window.innerWidth < 600)
  }

  const commatize = (word: any) => {
    const replaced = word.toString().replace('.', ',')
    return replaced.indexOf(',0') > 1 && replaced.slice(-2) === ','
      ? replaced.replace(',0', '')
      : replaced
  }

  const colorMapping = {
    id: '#202346',
    ekr: '#474A66',
    evp: '#DCD8C9',
    linke: '#de666f',
    s_d: '#DCD8C9',
    f_s: '#6F7187',
    re: '#DCD8C9',
    g_efa: '#DCD8C9',
  }
  // New text color mapping
  const textColorMapping = {
    id: '#f4f4f4',
    ekr: '#f4f4f4',
    evp: '#333',
    linke: '#f4f4f4',
    s_d: '#333',
    f_s: '#f4f4f4',
    re: '#333',
    g_efa: '#333',
  }

  // echarts options
  useEffect(() => {
    // Create Axis, series, and titles data by mapping over data
    const series: echarts.EChartsOption['series'] = []
    const yAxis: echarts.EChartsOption['yAxis'] = []
    const xAxis: echarts.EChartsOption['xAxis'] = []
    const titles: echarts.EChartsOption['title'] = []

    Object.keys(data).forEach((party, index) => {
      const partyData = data[party]
      const oldData = partyData['old']
      const newData = partyData['new']

      // Create a yAxis for each party
      yAxis.push({
        type: 'category',
        data: [party], // Display only the party name
        axisLabel: {
          interval: 0,
          show: true,
          formatter: function (value) {
            return `{barlabels|Heute}\n{barlabels|}\n{barlabels|Prognose}\n{barlabels|}\n{barlabels|}`
          },
          rich: {
            barlabels: {
              lineHeight: 11,
              color: colorMode === 'dark' ? '#aaa' : '#666',
            },
          },
        },
        splitLine: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        gridIndex: index,
        show: true,
      })

      // 'old' bar series
      series.push({
        name: 'old',
        type: 'bar',
        data: [oldData], // Display only 'old' data
        itemStyle: {
          color: colorMapping[party as keyof typeof colorMapping],
        },
        xAxisIndex: index,
        yAxisIndex: index, // Assign the yAxis index
        label: {
          show: true,
          position: 'insideRight',
          formatter: '{c}',
          color: textColorMapping[party as keyof typeof textColorMapping],
        },
        silent: true,
      })

      // 'new' bar series
      series.push({
        name: 'new',
        type: 'bar',
        data: [newData], // Display only 'new' data
        itemStyle: {
          color: colorMapping[party as keyof typeof colorMapping],
        },
        xAxisIndex: index,
        yAxisIndex: index, // Assign the yAxis index
        label: {
          show: true,
          position: 'right',
          offset: [window.innerWidth < 600 ? -28 : -29, 0],
          formatter: function (params) {
            const difference = Number(params.data) - Number(oldData) // Convert params.data and oldData to numbers
            return `{value|${params.data}}{difference${
              difference >= 0 ? 'Positive' : 'Negative'
            }| ${difference > 0 ? '+' + difference : difference}}`
          },
          rich: {
            value: {
              color: textColorMapping[party as keyof typeof textColorMapping],
              align: 'left',
            },
            differencePositive: {
              color: '#2CA29F',
              fontWeight: 'bold',
              fontSize: 13,
              align: 'right',
              padding: [0, 0, 0, 10],
            },
            differenceNegative: {
              color: colorMode === 'dark' ? '#f4f4f4' : '#333',
              fontWeight: 'bold',
              fontSize: 13,
              align: 'right',
              padding: [0, 0, 0, 10],
            },
          },
        },
        silent: true,
      })

      // Spacer series
      series.push({
        type: 'bar',
        itemStyle: {
          color: 'transparent', // Set the color to red
        },
        markLine: {
          silent: true,
          symbol: 'none',
          data: [
            {
              yAxis: 0,
              lineStyle: {
                color: colorMode === 'dark' ? '#444' : '#ccc',
                type: 'dotted',
              },
            },
          ],
        },
        silent: true, // Disable interactions
        xAxisIndex: index,
        yAxisIndex: index, // Assign the yAxis index
      })

      xAxis.push({
        type: 'value',
        // Calculate the maximum value from data
        max:
          Math.max(
            ...Object.values(data).map((party: any) =>
              Math.max(party.old, party.new)
            )
          ) + (window.innerWidth ? 25 : 20),
        splitLine: {
          show: false,
        },
        gridIndex: index,
        show: true,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
      })

      // Add title for each party
      titles.push({
        text: partyData.title, // Use the title from data
        left: 0,
        top:
          index === 0
            ? 3
            : index === 1
            ? window.innerWidth < 600
              ? '12.5%'
              : '12.5%'
            : index === 2
            ? window.innerWidth < 600
              ? '26.5%'
              : '24.5%'
            : index === 3
            ? window.innerWidth < 600
              ? '38%'
              : '36.5%'
            : index === 4
            ? window.innerWidth < 600
              ? '52%'
              : '48.5%'
            : index === 5
            ? window.innerWidth < 600
              ? '63.5%'
              : '60.5%'
            : index === 6
            ? window.innerWidth < 600
              ? '75%'
              : '72.5%'
            : index === 7
            ? window.innerWidth < 600
              ? '86.5%'
              : '84.5%'
            : index * 12 + '%', // Increase the spacing underneath the title
        textStyle: {
          color: colorMode === 'dark' ? '#f4f4f4' : '#202346',
          fontSize: window.innerWidth < 600 ? 13 : 13.5,
          lineHeight: 17,
          fontWeight: 'bold',
        },
      })
    })

    // echart options
    const chartOptions: ReactEChartsProps['option'] = {
      title: titles, // Add titles to the chart options
      height: isMobile ? '130px' : '150px',
      textStyle: {
        fontFamily:
          '"IBM Plex Sans","Helvetica",Arial,Verdana,Helvetica,sans-serif',
      },
      tooltip: {
        show: false,
        formatter: function (params: any): string {
          // console.log('tooltip params', params)
          let tooltipContent = ''
          if (params) {
            const year = params[0].axisValue
            tooltipContent += '<b class="year">' + year + '</b><hr/>'
            const parties = params.filter((item: any) => {
              return item.value !== '0'
            }, [])
            parties.forEach((item: any) => {
              if (item.value > 0) {
                tooltipContent += `<div class="square" style="background-color: ${item.color};">&nbsp;</div>`
                tooltipContent += `${item.seriesName}: ${commatize(
                  item.value
                )}<br />`
              }
            })
          }
          return '<div class="tooltip">' + tooltipContent + '</div>'
        },
        valueFormatter: function (value) {
          return value ? commatize(value) + '%' : 'missing'
        },
        trigger: 'axis',
        axisPointer: {
          axis: 'y',
          type: 'shadow', // 'shadow' as default; can also be 'line' or 'shadow'
        },
        position: { right: '20%', top: 40 },
      },
      animation: false,
      grid: [
        {
          top: window.innerWidth < 600 ? '3.5%' : '3.5%',
          width: '100%',
          height: '11%',
          left: 5,
          containLabel: true,
        },
        {
          top: window.innerWidth < 600 ? '17.5%' : '15.5%',
          height: '11%',
          width: '100%',
          left: 5,
          containLabel: true,
        },
        {
          top: window.innerWidth < 600 ? '29%' : '27.5%',
          height: '11%',
          width: '100%',
          left: 5,
          containLabel: true,
        },
        {
          top: window.innerWidth < 600 ? '43%' : '39.5%',
          height: '11%',
          width: '100%',
          left: 5,
          containLabel: true,
        },
        {
          top: window.innerWidth < 600 ? '54.5%' : '51.5%',
          height: '11%',
          width: '100%',
          left: 5,
          containLabel: true,
        },
        {
          top: window.innerWidth < 600 ? '66%' : '63.5%',
          height: '11%',
          width: '100%',
          left: 5,
          containLabel: true,
        },
        {
          top: window.innerWidth < 600 ? '77.5%' : '75.5%',
          height: '11%',
          width: '100%',
          left: 5,
          containLabel: true,
        },
        {
          top: window.innerWidth < 600 ? '89.5%' : '87.5%',
          height: '11%',
          width: '100%',
          left: 5,
          containLabel: true,
        },
      ],
      xAxis, // see for each loop above
      yAxis, // see for each loop above
      series, // see for each loop above
    }
    setChartOptions(chartOptions)
  }, [isMobile, colorMode, data])

  return (
    <Barchart className={isMobile ? 'mobile' : 'desktop'}>
      <div className='graphic'>
        <ReactECharts
          option={chartOptions}
          theme={colorMode === 'dark' ? 'ta-dark' : 'ta'}
          height={isMobile ? '190px' : '700px'}
          onClicked={(params) => {
            console.log('clicked', params)
          }}
        ></ReactECharts>
      </div>
    </Barchart>
  )
}
const Barchart = styled.div`
  color: var(--text-color);
`

export default BarChart
